import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import { Box, Stack, Typography } from "@mui/material";

export type TrendDirection = "up" | "down";
export type StatusType = "high" | "normal";

export type DetailItem = {
  code: string;
  value: string;
  status: string;
  type: StatusType;
};

type LabGraphProps = {
  value: string;
  trend: string;
  direction: TrendDirection;
  bars: number[];
  status: string;
  statusType: StatusType;
  detailDate: string;
  detailItems?: DetailItem[];
};

const yAxisLabels = ["40", "30", "20", "10", "5", "0"];
const xAxisLabels = ["16 \u015eubat", "17 \u015eubat", "18 \u015eubat"];

const getStatusColor = (type: StatusType) => (type === "high" ? "#BC0003" : "#2D9F75");
const getStatusDotColor = (type: StatusType) => (type === "high" ? "#BC0003" : "#48CAA1");
const getBarColor = (type: StatusType, index: number) => {
  if (index < 2) return "#8FDFC6";
  return type === "high" ? "#7C0E29" : "#38C296";
};

export default function LabGraph({
  value,
  trend,
  direction,
  bars,
  status,
  statusType,
  detailDate,
  detailItems,
}: Readonly<LabGraphProps>) {
  const trendColor = direction === "up" ? "#2D9F75" : "#BC0003";

  return (
    <Box
      sx={{
        alignSelf: "stretch",
        flex: "1 1 0",
        p: 2,
        background: "#fff",
        boxShadow: "0px 0px 0px 0.5px rgba(0, 0, 0, 0.07)",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative",
      }}
    >
      <Stack direction="row" alignItems="flex-end" spacing={1}>
        <Typography
          sx={{
            color: "#0A0D14",
            fontSize: 20,
            fontFamily: "Poppins",
            fontWeight: 400,
            lineHeight: "24px",
          }}
        >
          {value}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {direction === "up" ? (
              <NorthRoundedIcon sx={{ width: 10, height: 10, color: trendColor }} />
            ) : (
              <SouthRoundedIcon sx={{ width: 10, height: 10, color: trendColor }} />
            )}
            <Typography
              sx={{
                color: trendColor,
                fontSize: 12,
                fontFamily: "Inter Display",
                fontWeight: 600,
                lineHeight: "16.8px",
              }}
            >
              {trend}
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: "#868C98",
              fontSize: 12,
              fontFamily: "Poppins",
              fontWeight: 400,
              lineHeight: "16.8px",
            }}
          >
            {"Önceki Sonuca Göre"}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ display: "flex", gap: 1, alignItems: "stretch", minHeight: 206 }}>
        <Stack justifyContent="space-between" alignItems="flex-end" sx={{ width: 22 }}>
          {yAxisLabels.map((label) => (
            <Typography
              key={label}
              sx={{
                color: "rgba(134, 140, 152, 0.50)",
                fontSize: 8,
                fontFamily: "Poppins",
                fontWeight: 400,
                textTransform: "uppercase",
                lineHeight: "12px",
                letterSpacing: "0.16px",
              }}
            >
              {label}
            </Typography>
          ))}
        </Stack>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", position: "relative" }}>
          <Box
            sx={{
              flex: 1,
              borderLeft: "1px solid rgba(10, 13, 20, 0.05)",
              borderBottom: "1px solid rgba(10, 13, 20, 0.05)",
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              px: 2,
              pb: 0,
            }}
          >
            {bars.map((bar, index) => (
              <Box
                key={`${index}-${bar}`}
                sx={{
                  flex: 1,
                  height: `${bar * 5.4}px`,
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  "&:hover .lab-hover-card": {
                    opacity: 1,
                    transform: "translate(-50%, -8px)",
                    pointerEvents: "auto",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "100%",
                    background: getBarColor(statusType, index),
                    boxShadow: "0px -14px 21px rgba(255, 255, 255, 0.40) inset",
                    borderTopLeftRadius: "2px",
                    borderTopRightRadius: "2px",
                    borderLeft: "1px solid rgba(0, 0, 0, 0.06)",
                    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                    borderRight: "1px solid rgba(0, 0, 0, 0.06)",
                    opacity: 0.92,
                  }}
                />

                <Box
                  className="lab-hover-card"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    transform: "translate(-50%, 0px)",
                    width: detailItems ? 176 : 148,
                    background: "#fff",
                    boxShadow: "0px 0px 0px 1px #F2F2F2, 0px 5px 10px -2.5px rgba(42, 51, 70, 0.27)",
                    borderRadius: "10px",
                    overflow: "hidden",
                    zIndex: 3,
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 0.18s ease, transform 0.18s ease",
                  }}
                >
                  {detailItems ? (
                    <>
                      <Box sx={{ px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 0.9 }}>
                        <Typography sx={{ color: "#868C98", fontSize: 10, fontFamily: "Poppins", fontWeight: 500, lineHeight: "14px" }}>
                          {"De\u011fer"}
                        </Typography>
                        {detailItems.map((item) => (
                          <Stack key={item.code} direction="row" alignItems="center" spacing={0.75}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                background: getStatusDotColor(item.type),
                                borderRadius: "3px",
                                boxShadow: "0px 0.5px 3px rgba(0, 0, 0, 0.15)",
                                border: "0.5px solid white",
                              }}
                            />
                            <Typography
                              sx={{
                                color: item.code === "wbc" ? "#0A0D14" : "#0F172A",
                                fontSize: 10,
                                fontFamily: "Poppins",
                                fontWeight: item.code === "wbc" ? 500 : 400,
                                lineHeight: "14px",
                              }}
                            >
                              {item.code}
                            </Typography>
                            <Typography sx={{ color: "#0A0D14", fontSize: 12, fontFamily: "Poppins", fontWeight: 500, lineHeight: "16px" }}>
                              {item.value}
                            </Typography>
                            <Typography sx={{ color: getStatusColor(item.type), fontSize: 10, fontFamily: "Poppins", fontWeight: 400, lineHeight: "14px" }}>
                              {item.status}
                            </Typography>
                          </Stack>
                        ))}
                        <Typography sx={{ color: "#0F172A", fontSize: 10, fontFamily: "Poppins", fontWeight: 400, lineHeight: "14px" }}>
                          {"Devamını Gör .."}
                        </Typography>
                      </Box>
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Typography sx={{ color: "#868C98", fontSize: 10, fontFamily: "Poppins", fontWeight: 400, lineHeight: "14px" }}>
                          {detailDate}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography sx={{ color: "#868C98", fontSize: 11, fontFamily: "Poppins", fontWeight: 500, lineHeight: "16px" }}>
                          {"De\u011fer"}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              background: getStatusDotColor(statusType),
                              borderRadius: "3px",
                              boxShadow: "0px 0.5px 3px rgba(0, 0, 0, 0.15)",
                              border: "0.5px solid white",
                            }}
                          />
                          <Typography sx={{ color: "#0A0D14", fontSize: 16, fontFamily: "Poppins", fontWeight: 500, lineHeight: "20px" }}>
                            {value}
                          </Typography>
                          <Typography sx={{ color: getStatusColor(statusType), fontSize: 12, fontFamily: "Poppins", fontWeight: 400, lineHeight: "16px" }}>
                            {status}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Typography sx={{ color: "#868C98", fontSize: 11, fontFamily: "Poppins", fontWeight: 400, lineHeight: "15px" }}>
                          {detailDate}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 2, pt: 0.75, borderTop: "1px solid rgba(0, 0, 0, 0.05)", display: "flex", gap: 2 }}>
            {xAxisLabels.map((label, index) => (
              <Box key={label} sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{
                    color: index === xAxisLabels.length - 1 ? "#0A0D14" : "rgba(134, 140, 152, 0.50)",
                    fontSize: 8,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    lineHeight: "12px",
                    letterSpacing: "0.16px",
                    textAlign: "center",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
